import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, Th } from '@/components/Table';
import {
  FreelancerInvoiceSubmissionResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

import FreelancerInvoicesTableRow from './FreelancerInvoicesTableRow';

const StyledTable = styled(Table)`
  border-collapse: separate;
  border-spacing: 0;
  th {
    font-size: 11px;
  }
  th,
  td {
    padding: 19px 16px;
    font-size: 12px;
    font-weight: 700;
    &:nth-child(1) {
      padding-left: 24px;
    }
    &:last-child {
      width: 56px;
      min-width: 56px;
      height: 64px;
      position: sticky;
      right: -1px;
      top: 0;
      bottom: 0;
      z-index: 1;
    }
  }
`;

const StatusTh = styled(Th)`
  position: sticky;
  box-shadow: -1px 0 0 ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
  right: 56px;
  top: 0;
  bottom: 0;
  z-index: 1;
`;

const CommentTh = styled(Th)`
  width: 140px;
  min-width: 140px;
`;

const InvoiceIdTh = styled(Th)`
  width: 138px;
  min-width: 138px;
`;

const FreelancersInvoicesTable = () => {
  const freelancerId = useSelector(
    (rootState: RootState) => (rootState as RootState).auth.user!.freelancer?.id,
  );
  const fetchInvoices = ({
    pageParam = '/v1/payments/freelancer-invoice-submissions',
  }) => {
    return api.get<
      never,
      PaginatedResponse<FreelancerInvoiceSubmissionResource>
    >(pageParam, {
      params: {
        'filter[freelancer_id]': freelancerId,
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerInvoiceSubmissionResource>>(
    ['freelancer_invoice_submissions'],
    fetchInvoices,
    {
      getNextPageParam: (lastPage) => lastPage.links.next,
    },
  );

  const submissions = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<
        FreelancerInvoiceSubmissionResource[]
      >((acc, currKey: any) => {
        return [...acc, ...data!.pages[currKey].data];
      }, []),
    [data],
  );

  return (
    <div>
      <SectionHeading>invoice requests</SectionHeading>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Request Date</Th>
              <Th>Company</Th>
              <Th>Hours</Th>
              <Th>Amount</Th>
              <Th>Bonus</Th>
              <Th>VAT</Th>
              <Th>Total</Th>
              <CommentTh>Comment</CommentTh>
              <InvoiceIdTh>Invoice ID</InvoiceIdTh>
              <Th>Payment Purpose</Th>
              <Th>Invoice</Th>
              <StatusTh>Team Lead Approval</StatusTh>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!submissions && submissions.length > 0}
            >
              {submissions?.map((submission) => (
                <FreelancerInvoicesTableRow
                  key={submission.id}
                  submission={submission}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </div>
  );
};

export default FreelancersInvoicesTable;
